import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, RefreshCw, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { Button } from '../ds/Button';
import { Card } from '../ds/Card';
import { useUploadWorkflow } from '../../hooks/useUploadWorkflow';
import { ErrorBoundary } from '../ErrorBoundary';

/**
 * CSVUploader - Isolated CSV upload component
 *
 * Features:
 * - Drag and drop support
 * - Progress indicator
 * - Error handling
 * - Cancellation support
 */
export function CSVUploader({ category, clubType, dexterity, onUploadComplete }) {
  const {
    stage,
    error,
    progress,
    data,
    upload,
    cancel,
    reset,
    isUploading,
    hasError,
    isComplete,
  } = useUploadWorkflow();

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    try {
      const result = await upload(file, '/analyze/upload', {
        mode: 'matrix',
        category,
        club_type: clubType,
        dexterity,
        playing_conditions: false,
      });

      if (onUploadComplete) {
        onUploadComplete(result);
      }
    } catch (err) {
      // Error is handled by useUploadWorkflow
      console.error('CSV upload failed:', err);
    }
  }, [upload, category, clubType, dexterity, onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
    disabled: isUploading,
  });

  return (
    <ErrorBoundary>
      <Card padding="p-6">
        {/* Upload Area */}
        {!isComplete && !hasError && (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary'
            } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input {...getInputProps()} />

            {isUploading ? (
              <>
                <RefreshCw className="animate-spin mx-auto text-primary w-12 h-12" />
                <p className="mt-4 text-white font-medium">Uploading CSV...</p>
                {progress > 0 && (
                  <div className="mt-4 max-w-xs mx-auto">
                    <div className="bg-surface rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-primary h-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-text-muted mt-2">{progress}%</p>
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    cancel();
                  }}
                  className="mt-4 text-sm text-text-muted hover:text-text-primary"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <FileText className="mx-auto text-text-muted w-12 h-12" />
                <p className="mt-4 text-text-primary font-medium">
                  {isDragActive ? 'Drop CSV here' : 'Drop CSV here or click to browse'}
                </p>
                <p className="text-xs text-text-muted mt-2">
                  Supports: TrackMan, GCQuad, FlightScope, Mevo+, Foresight, SkyTrak
                </p>
              </>
            )}
          </div>
        )}

        {/* Success State - Minimized */}
        {isComplete && (
          <div className="bg-success/10 border border-success/30 rounded-lg p-3 flex items-center gap-3">
            <CheckCircle2 className="text-success flex-shrink-0" size={20} />
            <div className="flex-1">
              <p className="text-success text-sm font-medium">
                CSV uploaded - {data?.shots?.length || 0} shots processed
              </p>
            </div>
            <button
              onClick={reset}
              className="text-text-muted hover:text-text-primary"
              title="Upload different file"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Error State */}
        {hasError && (
          <div className="bg-status-error/10 border border-status-error/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-status-error flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-status-error font-medium">Upload failed</p>
                <p className="text-sm text-text-secondary mt-1">{error}</p>
                <button
                  onClick={reset}
                  className="text-sm text-primary hover:underline mt-2"
                >
                  Try again
                </button>
              </div>
              <button
                onClick={reset}
                className="text-text-muted hover:text-text-primary"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}
      </Card>
    </ErrorBoundary>
  );
}

export default CSVUploader;
